import {
  ConfirmModal,
  Flex,
  Table,
  TableRowMenuCell,
  Text,
} from '@/components';
import { useLocalTagPermission } from '@/features/authentication/_hooks/permission';
import { type TagBaseModel, TagTypeLabelModifier } from '@/features/tag/models';
import { useOrganizations } from '@/features/tenant-manager/organization/_hooks/organization';
import { useMessage } from '@/providers';
import {
  NEST_ITEM_CONNECTOR,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
  findUpperRecursive,
} from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useCreateLocalTag,
  useDeleteLocalTag,
  useLocalTags,
  useUpdateLocalTag,
} from '../hooks';
import { useDrawerState } from './DrawerStateProvider';
import { LocalTagFormDrawer } from './LocalTagFormDrawer';

export const LocalTagsPage: FC = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const localTags = useLocalTags();
  const organizations = useOrganizations();
  const editPermission = useLocalTagPermission(true);
  const { drawerState, setDrawerState } = useDrawerState();
  const { mutateAsync: createLocalTagAsync } = useCreateLocalTag();
  const { mutateAsync: updateLocalTagAsync } = useUpdateLocalTag();
  const { mutateAsync: deleteLocalTagAsync } = useDeleteLocalTag();
  const [activeModalType, setActiveModalType] = useState<'delete' | null>(null);
  const [selectedLocalTag, setSelectedLocalTag] = useState<z.infer<
    typeof TagBaseModel
  > | null>(null);

  const isDrawerOpen = drawerState.open && drawerState.type === 'local-tag';

  const selectedTag =
    isDrawerOpen && drawerState.isEditing
      ? localTags.data?.find((d) => d.id === drawerState.id)
      : undefined;

  const handleCloseDrawer = (): void => {
    setDrawerState({ open: false });
  };

  return (
    <>
      <Table
        dataSource={localTags.data}
        rowKey="id"
        columns={[
          {
            title: t('scope-tag-type', { scope: t('local-tag') }),
            key: 'type',
            dataIndex: 'type',
            width: 250,
            render: (value) => TagTypeLabelModifier[value],
          },
          {
            title: t('scope-tag-name', { scope: t('local-tag') }),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: t('owner-organization'),
            key: 'organizationId',
            dataIndex: 'organizationId',
            render: (value) =>
              findUpperRecursive(value, organizations.data ?? [], [])
                .reverse()
                .map((d) => d.name)
                .join(NEST_ITEM_CONNECTOR),
          },
          {
            title: t('viewable-organization'),
            key: 'sharedOrganizationIds',
            dataIndex: 'sharedOrganizationIds',
            render: (_, { sharedOrganizationIds }) => {
              return sharedOrganizationIds.length > 0 ? (
                <Flex vertical>
                  {sharedOrganizationIds.map((id) => (
                    <Text
                      key={id}
                      text={findUpperRecursive(id, organizations.data ?? [], [])
                        .reverse()
                        .map((d) => d.name)
                        .join(NEST_ITEM_CONNECTOR)}
                      weight="regular"
                    />
                  ))}
                </Flex>
              ) : (
                <Text text={null} weight="regular" />
              );
            },
          },
          {
            title: t('start-fiscal-year'),
            key: 'startYear',
            dataIndex: 'startYear',
            width: 200,
          },
          {
            title: t('end-fiscal-year'),
            key: 'endYear',
            dataIndex: 'endYear',
            width: 200,
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'local-tag',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () => {
                            setSelectedLocalTag(record);
                            setActiveModalType('delete');
                          },
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <ConfirmModal
        open={activeModalType === 'delete' && selectedLocalTag !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-item-confirmation', {
          item: selectedLocalTag?.name ?? '',
        })}
        onOk={async () => {
          invariant(selectedLocalTag, t('unreachable-code'));

          try {
            await deleteLocalTagAsync(selectedLocalTag.id);
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              (async () => {
                setActiveModalType(null);
                setSelectedLocalTag(null);
              })(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={() => {
          setActiveModalType(null);
          setSelectedLocalTag(null);
        }}
        skipCheck
      />

      <LocalTagFormDrawer
        open={isDrawerOpen}
        selectedTag={selectedTag}
        onClose={handleCloseDrawer}
        onSubmit={async (values) => {
          invariant(
            drawerState.open && drawerState.type === 'local-tag',
            t('unreachable-code'),
          );
          drawerState.isEditing
            ? await updateLocalTagAsync({ ...values, id: drawerState.id })
            : await createLocalTagAsync({ ...values });
          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            (async () => {
              handleCloseDrawer();
            })(),
          ]);
        }}
      />
    </>
  );
};
